import * as React from 'react';

import {
  AnyCalendarDate,
  AnyDateTime,
  AnyTime,
  DateFormatter,
} from '@internationalized/date';
import { LucideDot } from 'lucide-react';

import {
  dateToCalendarDateTime,
  days,
  hours,
  minutes,
  weeks,
  years,
} from '../../../lib/dateTimeUtils';
import { cn } from '../../../lib/stylingUtils';
import { MaybeWrap } from '../../utilities';
import { Tooltip } from '../overlays';

const getDateDifference = (
  date1: Date,
  date2: Date,
  unit: 'minute' | 'hour' | 'day' | 'week' | 'year' | 'auto' = 'day',
) => {
  const timeDiff = date2.getTime() - date1.getTime();

  let autoUnit = undefined;

  if (unit === 'auto') {
    if (Math.abs(timeDiff) < hours(1)) {
      autoUnit = 'minute';
    } else if (Math.abs(timeDiff) < days(1)) {
      autoUnit = 'hour';
    } else if (Math.abs(timeDiff) < weeks(1)) {
      autoUnit = 'day';
    } else if (Math.abs(timeDiff) < years(1)) {
      autoUnit = 'week';
    } else {
      autoUnit = 'year';
    }
  }

  let diff = 0;

  switch (autoUnit ?? unit) {
    case 'year':
      diff = Math.ceil(timeDiff / years(1));
      break;
    case 'week':
      diff = Math.ceil(timeDiff / weeks(1));
      break;
    case 'day':
      diff = Math.ceil(timeDiff / days(1));
      break;
    case 'hour':
      diff = Math.ceil(timeDiff / hours(1));
      break;
    case 'minute':
      diff = Math.ceil(timeDiff / minutes(1));
      break;
    default:
      diff = Math.ceil(timeDiff / days(1));
      break;
  }

  return {
    diff,
    unit: (autoUnit ?? unit) as 'minute' | 'hour' | 'day' | 'week' | 'year',
  };
};

export type LocalizedDateTimeProps = {
  date?: AnyDateTime | Date;
  dateStyle?:
    | Intl.DateTimeFormatOptions['dateStyle']
    | 'relative'
    | 'relative-within-week';
  timeStyle?: Intl.DateTimeFormatOptions['timeStyle'];
  locale?: string;
};

// eslint-disable-next-line custom/require-component-classname
export const LocalizedDateTime = ({
  date,
  locale,
  dateStyle = 'medium',
  timeStyle = 'short',
}: LocalizedDateTimeProps) => {
  const parsedLocale = locale ?? navigator.languages[0];

  const nativeDate = React.useMemo(
    () =>
      date instanceof Date
        ? date
        : date
          ? new Date(
              date.year,
              date.month - 1,
              date.day,
              date.hour,
              date.minute,
              date.second,
              date.millisecond,
            )
          : undefined,
    [date],
  );

  const dateFormatter = React.useMemo(
    () =>
      new DateFormatter(parsedLocale, {
        dateStyle: dateStyle?.startsWith('relative')
          ? 'medium'
          : (dateStyle as Intl.DateTimeFormatOptions['dateStyle']),
      }),
    [parsedLocale, dateStyle],
  );

  const { diff: daysDifference } = React.useMemo(
    () =>
      nativeDate
        ? getDateDifference(new Date(), nativeDate, 'day')
        : { diff: undefined },
    [nativeDate],
  );

  const isRelative = React.useMemo(
    () =>
      daysDifference !== undefined &&
      (dateStyle === 'relative' ||
        (dateStyle === 'relative-within-week' && daysDifference < 7)),
    [dateStyle, daysDifference],
  );

  const formattedDate = React.useMemo(() => {
    const dateDiff = nativeDate
      ? getDateDifference(new Date(), nativeDate, 'auto')
      : undefined;

    return dateDiff && nativeDate
      ? isRelative
        ? new Intl.RelativeTimeFormat(parsedLocale, {
            style: 'short',
          }).format(dateDiff.diff, dateDiff.unit)
        : dateFormatter.format(nativeDate)
      : undefined;
  }, [nativeDate, isRelative, parsedLocale, dateFormatter]);

  const formattedTime = React.useMemo(
    () =>
      isRelative
        ? ''
        : new Intl.DateTimeFormat(parsedLocale, {
            timeStyle,
          }).format(nativeDate),
    [isRelative, nativeDate, parsedLocale, timeStyle],
  );

  if (!date) {
    return <>–</>;
  }

  return (
    <MaybeWrap
      condition={isRelative}
      wrapper={(children) => (
        <Tooltip
          tip={new Intl.DateTimeFormat(parsedLocale, {
            dateStyle: 'medium',
            timeStyle: 'short',
          }).format(nativeDate)}
        >
          {children}
        </Tooltip>
      )}
    >
      <span>
        {formattedDate} {formattedTime}
      </span>
    </MaybeWrap>
  );
};

export type LocalizedRelativeDateProps = {
  date?: AnyDateTime | Date;
  threshold?: 'week' | 'month';
  className?: string;
};

export const LocalizedRelativeDate = ({
  date,
  threshold = 'week',
  className,
}: LocalizedRelativeDateProps) => {
  const parsedLocale = navigator.languages[0];

  const nativeDate = React.useMemo(
    () =>
      date instanceof Date
        ? date
        : date
          ? new Date(date.year, date.month - 1, date.day, 0, 0, 0, 0)
          : undefined,
    [date],
  );

  const { diff: daysDifference } = React.useMemo(
    () =>
      nativeDate
        ? getDateDifference(new Date(), nativeDate)
        : { diff: undefined },
    [nativeDate],
  );

  const isRelative = React.useMemo(
    () =>
      daysDifference !== undefined &&
      (!threshold || daysDifference < (threshold === 'week' ? 7 : 30)),
    [daysDifference, threshold],
  );

  const longDateFormatted = React.useMemo(
    () =>
      new Intl.DateTimeFormat(parsedLocale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(nativeDate),
    [nativeDate, parsedLocale],
  );

  const [renderingVersion, setRenderingVersion] = React.useState(0);
  const renderingVersionRef = React.useRef(renderingVersion);
  renderingVersionRef.current = renderingVersion;

  const relativeDateFormatted = React.useMemo(() => {
    void [renderingVersion];

    if (!nativeDate) {
      return undefined;
    }

    const dateDiff = getDateDifference(new Date(), nativeDate, 'auto');

    return dateDiff.diff === 0
      ? 'now'
      : `${Math.abs(dateDiff.diff)}${dateDiff.unit.charAt(0)}`;
  }, [nativeDate, renderingVersion]);

  React.useEffect(() => {
    if (!nativeDate) return;

    const diff = getDateDifference(new Date(), nativeDate, 'auto');

    if (diff.unit === 'minute') {
      const interval = setInterval(() => {
        setRenderingVersion(renderingVersionRef.current + 1);
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [nativeDate]);

  if (!date) {
    return <span className={className}>–</span>;
  }

  return isRelative ? (
    <Tooltip tip={longDateFormatted}>
      <span className={className}>{relativeDateFormatted}</span>
    </Tooltip>
  ) : (
    <span className={className}>{longDateFormatted}</span>
  );
};

export type LocalizedDateProps = {
  className?: string;
  date?: AnyCalendarDate | AnyDateTime | Date;
  dateStyle?: Intl.DateTimeFormatOptions['dateStyle'];
  showRelativeDate?: boolean;
  wrapTriggerWithButton?: boolean;
  locale?: string;
  withTooltip?: boolean;
};

export const LocalizedDate = ({
  date: rawDate,
  dateStyle,
  locale,
  showRelativeDate = false,
  withTooltip = false,
  wrapTriggerWithButton,
  className,
}: LocalizedDateProps) => {
  const parsedLocale = locale ?? navigator.languages[0];

  const date =
    rawDate instanceof Date ? dateToCalendarDateTime(rawDate) : rawDate;

  const nativeDate = React.useMemo(
    () => (date ? new Date(date.year, date.month - 1, date.day) : undefined),
    [date],
  );

  const formattedDate = React.useMemo(() => {
    const formatter = new DateFormatter(parsedLocale, {
      dateStyle,
    });
    return nativeDate ? formatter.format(nativeDate) : '—';
  }, [parsedLocale, dateStyle, nativeDate]);

  const relativeDate = React.useMemo(() => {
    const formatter = new Intl.RelativeTimeFormat(parsedLocale, {
      style: 'short',
    });

    return nativeDate
      ? formatter.format(getDateDifference(new Date(), nativeDate).diff, 'day')
      : '—';
  }, [nativeDate, parsedLocale]);

  return (
    <div className={'@LocalizedDate contents'}>
      <Tooltip
        isDisabled={!showRelativeDate || !withTooltip}
        tip={relativeDate}
        wrapWithButton={wrapTriggerWithButton}
      >
        <div className={cn('flex items-center', className)}>
          {formattedDate}
          {showRelativeDate && (
            <>
              <LucideDot size={16} className={'text-neutral/40'} />
              <span className={'text-xs text-neutral/60'}>{relativeDate}</span>
            </>
          )}
        </div>
      </Tooltip>
    </div>
  );
};

export type LocalizedTimeProps = {
  time?: AnyDateTime | AnyTime | Date;
  timeStyle?: Intl.DateTimeFormatOptions['timeStyle'];
  locale?: string;
};

export const LocalizedTime = ({
  time: rawTime,
  timeStyle,
  locale,
}: LocalizedTimeProps) => {
  if (!rawTime) {
    return <>–</>;
  }

  const time =
    rawTime instanceof Date ? dateToCalendarDateTime(rawTime) : rawTime;

  const parsedLocale = locale ?? navigator.languages[0];

  const nativeDate = new Date(
    0,
    0,
    0,
    time.hour,
    time.minute,
    time.second,
    time.millisecond,
  );

  return (
    <div className="@LocalizedTime contents">
      {new Intl.DateTimeFormat(parsedLocale, {
        hour: 'numeric',
        minute: 'numeric',
        second: timeStyle === 'long' ? 'numeric' : undefined,
      }).format(nativeDate)}
    </div>
  );
};
